import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { Alert, Button, Container, Spinner } from "react-bootstrap";
import type { User } from "@ld-scorecard/shared";
import { useApi, ApiError } from "./api";
import AppShell from "./components/AppShell";
import DashboardPage from "./pages/DashboardPage";
import RequisitionPage from "./pages/RequisitionPage";
import ScorecardPage from "./pages/ScorecardPage";
import CandidateResultsPage from "./pages/CandidateResultsPage";
import AdminPage from "./pages/AdminPage";
import SignIn from "./pages/SignIn";

export const MeContext = createContext<User | null>(null);
export const useMe = () => useContext(MeContext);

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
      <div className="text-center">{children}</div>
    </Container>
  );
}

export default function App() {
  const auth = useAuth();
  const api = useApi();
  const ldClient = useLDClient();
  const [me, setMe] = useState<User | null>(null);
  const [meError, setMeError] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    let cancelled = false;
    api
      .me()
      .then((u) => {
        if (cancelled) return;
        setMe(u);
        // Re-key the LaunchDarkly context to the signed-in user so flags can
        // target by email/role (great for role-based rollouts).
        void ldClient?.identify({
          kind: "user",
          key: u.email,
          name: u.name ?? u.email,
          email: u.email,
          role: u.role,
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setMeError(e instanceof ApiError ? e.message : "Failed to load your profile");
      });
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, api, ldClient]);

  if (auth.isLoading) {
    return (
      <Centered>
        <Spinner animation="border" />
      </Centered>
    );
  }

  if (auth.error) {
    return (
      <Centered>
        <Alert variant="danger">
          <Alert.Heading>Sign-in error</Alert.Heading>
          <p>{auth.error.message}</p>
          <Button onClick={() => void auth.signinRedirect()}>Try again</Button>
        </Alert>
      </Centered>
    );
  }

  if (!auth.isAuthenticated) return <SignIn />;

  // Authenticated but the allowlist check (or profile load) failed.
  if (meError) {
    return (
      <Centered>
        <Alert variant="warning" style={{ maxWidth: 520 }}>
          <Alert.Heading>Access not granted</Alert.Heading>
          <p>{meError}</p>
          <Button variant="outline-secondary" onClick={() => void auth.removeUser()}>
            Sign out
          </Button>
        </Alert>
      </Centered>
    );
  }

  if (!me) {
    return (
      <Centered>
        <Spinner animation="border" />
      </Centered>
    );
  }

  return (
    <MeContext.Provider value={me}>
      <AppShell>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/requisitions/:reqId" element={<RequisitionPage />} />
          <Route path="/candidates/:id" element={<ScorecardPage />} />
          <Route path="/candidates/:id/results" element={<CandidateResultsPage />} />
          <Route
            path="/admin"
            element={me.role === "admin" ? <AdminPage /> : <Navigate to="/" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </MeContext.Provider>
  );
}
